import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { CreateVisitType } from '../../../visits/entity';
import { queryClient } from '../../../../shared/queries';
import { ApiErr } from '@shared/entity';
import type { PreviewVisit } from '../../pages/booking-page.hooks';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useCustomerAuth } from '@features/customer-reservations/CustomerAuthContext';
import {
  useReservationServicesQuery,
  useCreateCustomerVisitMutation,
} from '@features/customer-reservations/queries';

export const useBookingReservationSummary = (
  previewVisit: PreviewVisit | undefined,
  setPreviewVisit: (v: PreviewVisit | undefined) => void,
) => {
  const { t } = useIntlTranslations('customerAuth');
  const { control, reset } = useFormContext<CreateVisitType>();
  const { isAuthenticated } = useCustomerAuth();

  const selectedDate = useWatch({ control, name: 'date' });
  const serviceIds = useWatch({ control, name: 'serviceIds' });
  const dateTo = useWatch({ control, name: 'dateTo' });

  const { data: servicesData } = useReservationServicesQuery(isAuthenticated);
  const selectedService = servicesData?.services?.find(s => s.id === serviceIds);

  const createVisitMutation = useCreateCustomerVisitMutation();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleConfirm = () => {
    createVisitMutation.mutate(
      { date: selectedDate, dateTo, serviceIds, depositRequired: false, paidPrice: 0, deposit: 0 },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['myVisits'] });
          setSuccess(true);
          setPreviewVisit(undefined);
          reset();
          setTimeout(() => setSuccess(false), 5000);
        },
        onError: (err: ApiErr) => {
          setError(err.message);
          if (err.code === 409) {
            setTimeout(() => window.location.reload(), 1700);
          }
        },
      },
    );
  };

  const canConfirm = !!(selectedDate && dateTo && serviceIds && previewVisit);

  return {
    selectedDate,
    selectedService,
    previewVisit,
    createVisitMutation,
    error,
    success,
    canConfirm,
    handleConfirm,
    t,
  };
};
